import { reportError } from "utils/diagnostics";
import React, { useEffect, useRef, useState } from "react";
import { Alert, Dialog, DialogContent, DialogProps } from "@mui/material";
import {
  ConfigurationRestoreSupport,
  RestoreResult,
  getConfigurationRestoreSupport,
  pushFullConfigurationBackup,
} from "api/ConfigurationArchiveHelpers";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { PopupHeader } from "../popup/popup-header.component";
import { GhostButton, PrimaryButton } from "../Buttons";
import { showError } from "utils/notifications";
import style from "../popup/popup.module.css";

const MAX_FULL_BACKUP_BYTES = 4 * 1024 * 1024;
const VALID_EXTENSIONS = [".tar.gz", ".tgz"];

interface PushConfigDialogProps extends Omit<DialogProps, "onClose"> {
  open: boolean;
  onClose: () => void;
  deviceCount: number;
  targetSerialNumbers: string[];
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function PushConfigDialog({
  open,
  onClose,
  deviceCount,
  targetSerialNumbers,
}: PushConfigDialogProps): JSX.Element {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<RestoreResult>();
  const [error, setError] = useState("");
  const [support, setSupport] = useState<ConfigurationRestoreSupport & { targets: string }>();
  const targetKey = targetSerialNumbers.join(",");
  const currentSupport = support?.targets === targetKey ? support : undefined;
  const generation = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setProcessing(false);
      setResult(undefined);
      setError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    return () => {
      generation.current += 1;
    };
  }, [open, targetKey]);

  useEffect(() => {
    setSupport(undefined);
    if (!open) return undefined;
    let active = true;
    const targets = targetKey ? targetKey.split(",") : [];
    if (!targets.length || targets.length > 100) {
      setSupport({ targets: targetKey, supported: false, reason: "Select 1–100 MegaFi 2 devices." });
    } else {
      getConfigurationRestoreSupport(targets)
        .then((availability) => {
          if (active) setSupport({ ...availability, targets: targetKey });
        })
        .catch((failure) => {
          if (!active) return;
          reportError("config.fullBackup.support", failure);
          setSupport({
            targets: targetKey,
            supported: false,
            reason: "Unable to check full-backup support. Close and reopen this dialog to retry.",
          });
        });
    }
    return () => {
      active = false;
    };
  }, [open, targetKey]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(null);
    setError("");
    setResult(undefined);
    const file = event.target.files?.[0];
    if (!file) return;

    if (!VALID_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      showError("Please select a .tar.gz or .tgz file.");
      return;
    }
    if (file.size === 0) {
      showError("The selected file is empty.");
      return;
    }
    if (file.size > MAX_FULL_BACKUP_BYTES) {
      showError("Full backups must be 4 MiB or smaller.");
      return;
    }

    setSelectedFile(file);
  };

  const handleConfirm = async () => {
    if (processing || result || !currentSupport?.supported) return;
    if (!selectedFile) {
      showError("Please upload a config archive.");
      return;
    }

    const { current } = generation;
    setProcessing(true);
    setError("");
    try {
      const base64 = await fileToBase64(selectedFile);
      if (current !== generation.current) return;
      // Use cloud polling until MQTT full-backup restores are supported.
      const response = await pushFullConfigurationBackup(base64, [...targetSerialNumbers], "polling");
      if (current === generation.current) setResult(response);
    } catch (failure: any) {
      if (current !== generation.current) return;
      reportError("config.file.handleConfirm", failure);
      setError(failure?.response?.data?.error || "Unable to push the full backup. Please retry.");
    } finally {
      if (current === generation.current) setProcessing(false);
    }
  };

  const handleClose = () => {
    if (processing) return;
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  return (
    <Dialog className={style.container} fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <PopupHeader title="Push Device Configuration" />
      <DialogContent>
        <div style={{ marginBottom: "24px" }}>
          <p style={{ margin: 0, fontSize: "16px" }}>
            Push configuration to {deviceCount} MegaFi 2 {deviceCount === 1 ? "device" : "devices"}.
          </p>
        </div>

        <p style={{ marginTop: 0 }}>
          Upload a full backup downloaded from the portal or saved from the device. The backup will be delivered when
          each device next checks in.
        </p>
        {!currentSupport?.supported && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {currentSupport?.reason || "Checking full-backup support for the selected devices..."}
            {currentSupport && (
              <div>
                Remote full-backup restore needs compatible device firmware and cloud enablement. Selecting a file does
                not enable it.
              </div>
            )}
          </Alert>
        )}

        <div style={{ marginBottom: "24px" }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            style={{ textTransform: "none" }}
            disabled={processing}
          >
            Upload config archive
            <input
              ref={fileInputRef}
              type="file"
              accept=".tar.gz,.tgz,application/gzip,application/x-gzip"
              hidden
              aria-label="Configuration archive file"
              disabled={processing}
              onChange={handleFileChange}
            />
          </Button>
          {selectedFile && (
            <p style={{ margin: "8px 0 0", fontSize: "14px", color: "#666" }}>{selectedFile.name} loaded.</p>
          )}
        </div>

        <div style={{ marginBottom: "24px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
          <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
            The full backup will replace the saved configuration on the selected devices. Keep them powered on while it
            is applied.
          </p>
        </div>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {result && (
          <Alert severity={result.results.some((item) => item.error) ? "warning" : "success"} sx={{ mb: 2 }}>
            {result.results.map((item) => (
              <div key={item.serialNumber}>
                {item.serialNumber}:{" "}
                {item.error || (item.delivery === "mqtt" ? "Sent via MQTT." : "Queued for cloud polling.")}
              </div>
            ))}
            Device restore completion is not confirmed here.
          </Alert>
        )}

        <div className={style.alignContentEvenly}>
          <div style={{ marginRight: "16px" }}>
            <GhostButton onClick={handleClose} disabled={processing}>
              {result ? "Close" : "Cancel"}
            </GhostButton>
          </div>
          <PrimaryButton onClick={handleConfirm} disabled={processing || !!result || !currentSupport?.supported}>
            {processing ? "Pushing..." : "Push Config"}
          </PrimaryButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
